<template>
    <div class="tm-scope tp-dash">
        <div class="tp-dash__inner">
            <div class="tp-dash__top">
                <h1 class="tm-page-title tp-dash__role-title">{{ selectedRole ? selectedRole.name : 'Your career map' }}</h1>
                <!-- Skill demand differs by market: readiness and alternatives per location. -->
                <div v-if="target" class="tp-dash__filters">
                    <label class="tp-dash__toggle">
                        <input type="checkbox" v-model="includeInterns" @change="loading = true; loadDashboard()">
                        Include internships
                    </label>
                    <LocationSelect v-model="location" @change="loading = true; loadDashboard()" />
                </div>
            </div>

            <div v-if="banner" class="tp-banner" role="status">
                <span class="tp-banner__text">
                    <strong>{{ banner.added }} {{ banner.verified ? 'verified ' : '' }}{{ banner.added === 1 ? 'skill' : 'skills' }} added from “{{ banner.title }}”</strong>
                    <span v-if="banner.before !== null && target">{{ target.name }} readiness {{ banner.before }}% → {{ Math.round(target.readiness) }}%</span>
                </span>
                <button type="button" class="tp-banner__close" aria-label="Dismiss" @click="banner = null">
                    <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                </button>
            </div>

            <!-- No CV yet -- there's genuinely nothing to do here until one exists. -->
            <div v-if="!loading && !target && !hasCv" class="tm-card tp-empty">
                <h3 class="tp-empty__title">Upload your CV to see where you stand</h3>
                <button class="tm-btn-primary" @click="router.push({name:'dashboard-cv'})">
                    Go to my CV
                </button>
            </div>

            <!-- CV exists, just no target role set yet -- ask for it right here instead of
                 bouncing to the CV page, which has no job-title picker at all and so never
                 actually resolves this state. -->
            <div v-else-if="!loading && !target && hasCv" class="tm-card tp-empty">
                <h3 class="tp-empty__title">What job are you aiming for?</h3>
                <div class="tp-empty__picker">
                    <input
                        type="text" class="tm-field" placeholder="e.g. Data Scientist"
                        v-model="pickJobTitle"
                        @input="searchJobTitles"
                        @focus="pickJobShowDropdown = true"
                        @blur="onPickJobBlur"
                    >
                    <SuggestionDropdown
                        :items="pickJobResults" label-key="name"
                        :visible="pickJobShowDropdown"
                        @select="setTargetRole"
                    />
                </div>
                <p v-if="settingTarget" class="tm-faint" style="margin-top:10px">Setting your target role…</p>
            </div>

            <div v-else-if="loading" class="tp-loading">
                <div v-for="n in 3" :key="n" class="tp-skeleton"></div>
            </div>

            <!-- Left: an accordion of role cards (desired role first, then
                 alternatives by readiness — fixed order, only the open one
                 changes). Right: one ladder panel that never moves, its
                 content simply following whichever role is open. -->
            <div v-else class="tp-grid">
                <div class="tp-role-stack">
                    <template v-for="(role, idx) in visibleRoles" :key="role.id">
                        <div v-if="role.id === selectedRole.id" class="tm-card tp-readiness tm-reveal" :style="{ animationDelay: (idx * 60) + 'ms' }">
                            <span v-if="role.isTarget" class="tp-target-label">Target role</span>
                            <div class="tp-ring">
                                <svg width="168" height="168" viewBox="0 0 168 168" aria-hidden="true">
                                    <circle cx="84" cy="84" r="72" fill="none"
                                            stroke="var(--tm-border-soft)" stroke-width="13"/>
                                    <circle cx="84" cy="84" r="72" fill="none" stroke="url(#tpReady)"
                                            stroke-width="13" stroke-linecap="round"
                                            :stroke-dasharray="RING_C"
                                            :stroke-dashoffset="ringOffset"
                                            class="tp-ring__arc"/>
                                    <defs>
                                        <linearGradient id="tpReady" x1="0" y1="0" x2="1" y2="1">
                                            <stop offset="0%" stop-color="#1668E3"/>
                                            <stop offset="55%" stop-color="#21A0C4"/>
                                            <stop offset="100%" stop-color="#0E9F6E"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div class="tp-ring__num">
                                    <span class="tp-ring__pct">{{ Math.round(role.readiness) }}<span class="tp-ring__sign">%</span></span>
                                    <span class="tp-ring__cap tm-mono">Ready</span>
                                </div>
                            </div>


                            <div class="tp-legend">
                                <div class="tp-legend__row">
                                    <span class="tp-legend__key" style="background: var(--tm-have)"></span>
                                    You have it
                                    <span class="tp-legend__n tm-mono">{{ role.have.length }}</span>
                                </div>
                                <div class="tp-legend__row">
                                    <span class="tp-legend__key" style="background: var(--tm-gap)"></span>
                                    Missing
                                    <span class="tp-legend__n tm-mono">{{ role.missing.length }}</span>
                                </div>
                            </div>

                            <button class="tm-btn-secondary tp-readiness__btn"
                                    @click="router.push({name:'dashboard-job-detail',params:{id:role.id}})">
                                Role details
                            </button>
                        </div>

                        <div
                            v-else
                            class="tm-card tm-card--interactive tp-role tm-reveal"
                            :style="{ animationDelay: (idx * 60) + 'ms' }"
                            @click="selectedId = role.id"
                        >
                            <div class="tp-role__top">
                                <span class="tp-role__name">{{ role.name }}</span>
                                <span class="tp-role__pct" :style="{ color: readyColor(role.readiness) }">
                                    {{ Math.round(role.readiness) }}%
                                </span>
                            </div>
                            <span v-if="role.isTarget" class="tp-target-label tp-target-label--inline">Target role</span>
                            <span class="tm-meter tp-role__bar">
                                <span class="tm-meter__fill"
                                      :style="{ width: role.readiness + '%', background: readyColor(role.readiness) }"></span>
                            </span>
                            <p class="tp-role__meta">
                                {{ role.missing.length }} {{ role.missing.length === 1 ? 'skill' : 'skills' }} away
                            </p>
                        </div>
                    </template>

                    <button
                        v-if="hiddenRoleCount > 0"
                        type="button"
                        class="tp-rolelist__more"
                        @click="showAllRoles = true"
                    >Show {{ hiddenRoleCount }} more {{ hiddenRoleCount === 1 ? 'role' : 'roles' }}</button>
                    <button
                        v-else-if="showAllRoles && orderedRoles.length > 4"
                        type="button"
                        class="tp-rolelist__more"
                        @click="showAllRoles = false"
                    >Show less</button>
                    <YourCourses :occupation-id="target && target.id" :refresh-key="planKey" @completed="onCompleted" />
                </div>

                <!-- Gap ladder — fixed in place, always describing whichever
                     role is currently open on the left. -->
                <div class="tp-right">
                <div class="tp-ladder tm-reveal" style="animation-delay: 60ms">
                    <div class="tp-ladder__head">
                        <h2 class="tp-ladder__title">Learn these first</h2>
                    </div>

                    <div v-if="ladder.length">
                        <div v-for="(row, i) in ladder" :key="row.id || row.name" class="tp-lrow">
                            <span class="tp-lrow__rank tm-mono">{{ String(i + 1).padStart(2, '0') }}</span>
                            <span class="tp-lrow__main">
                                <span class="tp-lrow__name">{{ row.name }}</span>
                            </span>
                            <span class="tp-lrow__imp">
                                <span class="tm-meter">
                                    <span class="tm-meter__fill" :style="{ width: impPct(row.importance) + '%' }"></span>
                                </span>
                                <span class="tp-lrow__impn tm-mono">{{ Math.round(impPct(row.importance)) }}%</span>
                            </span>
                            <span v-if="row.has" class="tm-chip tm-chip--have tp-status">You have it</span>
                            <!-- The "Missing" chip is the affordance: hover / focus splits it into
                                 "+ Add" (I have it, put it on my CV) | "Learn" (courses drawer). -->
                            <span v-else-if="justAddedIds.has(row.id)" class="tm-chip tm-chip--have tp-status">Added</span>
                            <span v-else-if="addingSkillId === row.id" class="tm-chip tm-chip--gap tp-status">Adding…</span>
                            <span v-else class="tm-chip tm-chip--gap tp-status tp-split" :class="{ 'tp-split--solo': courseCounts[row.id] === 0 }">
                                <span class="tp-split__rest" aria-hidden="true">Missing</span>
                                <button type="button" class="tp-split__half tp-split__half--add"
                                        :aria-label="`Add ${row.name} to my CV`" @click="addMissingSkill(row)">+ Add</button>
                                <button v-if="courseCounts[row.id] !== 0" type="button" class="tp-split__half tp-split__half--learn"
                                        :aria-label="`Courses for ${row.name}`" @click="openLearn(row)">Learn</button>
                            </span>
                        </div>
                        <button
                            v-if="ladderFull.length > ladder.length"
                            type="button"
                            class="tp-rolelist__more tp-ladder__more"
                            @click="ladderVisibleCount += 12"
                        >Show {{ Math.min(12, ladderFull.length - ladder.length) }} more</button>
                    </div>
                    <p v-else class="tm-faint">No skill data available for this role yet.</p>
                </div>

                </div>
            </div>
        </div>
        <CourseDrawer v-if="selectedRole" :skill="learnSkill" :occupation-id="selectedRole.id" :job-name="selectedRole.name"
                      :location="location" @close="learnSkill = null" @saved="planKey++" />
    </div>
</template>

<script>
import { CareerApiService } from '@/services/CareerApiService';
import { UserInfoApiService } from '@/services/UserInfoApiService';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import SuggestionDropdown from '@/components/SuggestionDropdown.vue';
import LocationSelect from '@/components/LocationSelect.vue';
import CourseDrawer from '@/components/CourseDrawer.vue';
import YourCourses from '@/components/YourCourses.vue';
import { CourseApiService } from '@/services/CourseApiService';

const RING_C = 2 * Math.PI * 72;
const VISIBLE_ALT_COUNT = 3;

export default defineComponent({
    components: { SuggestionDropdown, LocationSelect, CourseDrawer, YourCourses },
    setup() {
        const router = useRouter()
        const loading = ref(true)
        const location = ref(localStorage.getItem('topment_location') || 'Global')
        watch(location, v => { try { localStorage.setItem('topment_location', v) } catch (e) { /* private mode */ } })
        // Internships are never suggested to someone unless they ask for them.
        const includeInterns = ref(localStorage.getItem('topment_include_interns') === '1')
        watch(includeInterns, v => { try { localStorage.setItem('topment_include_interns', v ? '1' : '0') } catch (e) { /* private mode */ } })
        const hasCv = ref(false)      // distinguishes "no CV yet" from "CV exists, no target role"
        const jobs = ref([])          // [{ id, name, isTarget, have[], missing[], readiness }]
        const ringPct = ref(0)        // animated separately so the arc draws in

        // Which single role's full ring + ladder is currently open. Null means
        // "use the desired role" — set explicitly once the user clicks another
        // role's collapsed box.
        const selectedId = ref(null)
        const showAllRoles = ref(false)

        const target = computed(() => jobs.value.find(j => j.isTarget) || null)
        const selectedRole = computed(() =>
            jobs.value.find(j => j.id === selectedId.value) || target.value
        )

        // Fixed order: desired role first, then alternatives by readiness.
        // This never changes when the user opens a different role — only
        // which entry renders expanded does.
        const orderedRoles = computed(() => {
            if (!target.value) return []
            const others = jobs.value.filter(j => !j.isTarget).sort((a, b) => b.readiness - a.readiness)
            return [target.value, ...others]
        })

        // The role currently open always has to be on screen — if the user
        // opened one that only "Show more" had revealed and then collapses
        // the list again, the base slice alone would drop the very role
        // whose ring/ladder is showing. Keep it, in its normal sorted spot,
        // rather than losing the expanded block entirely.
        const visibleRoles = computed(() => {
            if (showAllRoles.value) return orderedRoles.value
            const base = orderedRoles.value.slice(0, VISIBLE_ALT_COUNT + 1)
            const openId = selectedRole.value && selectedRole.value.id
            if (openId != null && !base.some(r => r.id === openId)) {
                return orderedRoles.value.filter(r => base.some(b => b.id === r.id) || r.id === openId)
            }
            return base
        })

        const hiddenRoleCount = computed(() =>
            showAllRoles.value ? 0 : Math.max(0, orderedRoles.value.length - visibleRoles.value.length)
        )

        // Every skill for the currently-open role, most important first. This is
        // the same have/missing/importance data the force graph was drawing —
        // sorted, so the first row is the answer to "what do I learn next".
        // Full list (unpaginated) -- a role can have 80+ missing skills, most of them
        // long-tail/low-importance (the threshold that lets a skill into a role's
        // candidate pool at all is low); showing the true count but paginating the
        // ladder lets someone actually check whether the tail is worth learning,
        // instead of a hard-capped 12 with no way to see further.
        const ladderFull = computed(() => {
            if (!selectedRole.value) return []
            return [...selectedRole.value.missing.map(s => ({ ...s, has: false })),
                    ...selectedRole.value.have.map(s => ({ ...s, has: true }))]
                .sort((a, b) => {
                    // missing skills outrank held ones at equal importance
                    if (a.has !== b.has) return a.has ? 1 : -1
                    return b.importance - a.importance
                })
        })
        const ladderVisibleCount = ref(12)
        const ladder = computed(() => ladderFull.value.slice(0, ladderVisibleCount.value))

        const ringOffset = computed(() => RING_C * (1 - ringPct.value / 100))

        // Importance arrives as a 0-1 fraction (confirmed against real data —
        // the top-ranked missing skills for a role commonly sit right at 1.0),
        // not 0-10. Clamped in case a value ever lands outside that range.
        function impPct(v) {
            return Math.max(0, Math.min(100, (v || 0) * 100))
        }

        function readyColor(pct) {
            if (pct >= 75) return 'var(--tm-have)'
            if (pct >= 45) return 'var(--tm-partial)'
            return 'var(--tm-gap)'
        }

        // Weighted: a missing critical skill should cost more than a missing
        // nice-to-have. The old count-based match treated them identically.
        function weightedReadiness(have, missing) {
            const w = list => list.reduce((sum, s) => sum + (s.importance || 0), 0)
            const total = w(have) + w(missing)
            if (!total) return have.length + missing.length
                ? (have.length / (have.length + missing.length)) * 100
                : 0
            return (w(have) / total) * 100
        }

        function splitSkills(skills, userSkillIds) {
            const have = [], missing = []
            for (const s of (skills || [])) {
                const entry = {
                    name: s.name,
                    id: s.skill_id,
                    importance: Math.round((s.importance || 0) * 100) / 100,
                }
                if (userSkillIds.has(s.skill_id)) have.push(entry)
                else missing.push(entry)
            }
            return { have, missing }
        }

        function animateRing(to) {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                ringPct.value = to
                return
            }
            requestAnimationFrame(() => { ringPct.value = to })
        }

        // Pulled out of onMounted so adding a missing skill can re-run the exact same
        // load -- every role's have/missing/readiness is recomputed from scratch against
        // the user's real (now-updated) skill set, not patched in place. That's what makes
        // "add to CV" actually move alternative-role suggestions, not just the open one.
        async function loadDashboard() {
            const userId = localStorage.getItem('user_id')
            if (!userId) return router.push({ name: 'signin' })

            try {
                const [cvRes, jobRes] = await Promise.all([
                    UserInfoApiService.GetUserCvs(userId),
                    CareerApiService.getJobId(userId),
                ])

                const cvs = cvRes.data.user_all_cvs || []
                const cvId = cvs.length ? cvs[cvs.length - 1].cvid : null
                if (cvId) localStorage.setItem('cv_id', cvId)
                hasCv.value = !!cvId

                const targetName = jobRes.data.user_job && jobRes.data.user_job[0]
                const targetId = jobRes.data.user_job && jobRes.data.user_job[1]
                if (!targetId || !cvId) { loading.value = false; return }

                const gapRes = await CareerApiService.MissingSkills(targetId, cvId, location.value, includeInterns.value)
                // skill_details ids, same ids every job's skill list uses -- no name matching
                const userSkillIds = new Set(
                    (gapRes.data.user_skills || []).map(s => s.skill_id)
                )

                const roles = [{ id: targetId, name: targetName, isTarget: true }]
                for (const alt of (gapRes.data.alternative_jobs || [])) {
                    // Belt-and-suspenders against the target job appearing in its own
                    // "alternative jobs" match (fixed server-side, but two roles sharing an
                    // id would render as two expanded "Target role" cards -- see below,
                    // expansion is keyed on id).
                    if (alt.occupation_id === targetId) continue
                    roles.push({ id: alt.occupation_id, name: alt.job_name, isTarget: false })
                }

                const skillSets = await Promise.all(
                    roles.map(r => CareerApiService.getSkillsForJob(r.id, location.value)
                        .then(res => res.data.skills)
                        .catch(() => []))
                )

                jobs.value = roles.map((r, i) => {
                    const { have, missing } = splitSkills(skillSets[i], userSkillIds)
                    return { ...r, have, missing, readiness: weightedReadiness(have, missing) }
                })

                loading.value = false
            } catch (e) {
                loading.value = false
            }
        }

        onMounted(loadDashboard)

        // Inline "what job are you aiming for" picker for the has-a-CV-but-no-target-role
        // empty state -- previously just sent the user to the CV page, which has no
        // job-title picker at all, so it could never actually resolve that state.
        const pickJobTitle = ref('')
        const pickJobResults = ref([])
        const pickJobShowDropdown = ref(false)
        const settingTarget = ref(false)
        function searchJobTitles() {
            if (!pickJobTitle.value || pickJobTitle.value.length < 2) {
                pickJobResults.value = []
                return
            }
            CareerApiService.OccupSearch(pickJobTitle.value).then((res) => {
                pickJobShowDropdown.value = true
                pickJobResults.value = res.data.jobs || []
            })
        }
        function onPickJobBlur() {
            setTimeout(() => { pickJobShowDropdown.value = false }, 200)
        }
        async function setTargetRole(job) {
            pickJobShowDropdown.value = false
            settingTarget.value = true
            const userId = localStorage.getItem('user_id')
            try {
                await CareerApiService.DesiredJob(job.id, userId, job.name)
                loading.value = true
                await loadDashboard()
            } finally {
                settingTarget.value = false
            }
        }

        // Re-draw the ring whenever the open role changes — this also covers
        // the initial draw, since selectedRole flips from null to the target
        // role the moment jobs.value loads.
        watch(selectedRole, (role) => {
            if (role) animateRing(role.readiness)
            ladderVisibleCount.value = 12
        })

        // "Missing" chip on a ladder row doubles as the add-to-CV control (hover reveals
        // "+ Add") -- deliberately not a separate, prominent button. Persists for real
        // (via the same /skill/<userid> endpoint the CV page uses, with cvid so it's
        // actually linked -- see the cvid persistence fix), then reloads the whole
        // dashboard so every role's readiness reflects it, not just the open one.
        const addingSkillId = ref(null)
        const justAddedIds = ref(new Set())
        async function addMissingSkill(row) {
            if (addingSkillId.value || justAddedIds.value.has(row.id)) return
            const userId = localStorage.getItem('user_id')
            const cvId = localStorage.getItem('cv_id')
            if (!userId || !cvId) return
            addingSkillId.value = row.id
            try {
                await UserInfoApiService.saveSkills([row.name], userId, cvId)
                justAddedIds.value = new Set([...justAddedIds.value, row.id])
                await loadDashboard()
            } finally {
                addingSkillId.value = null
            }
        }

        // Courses: course counts per missing skill (Learn shows only when there are some),
        // the Learn drawer, and the follow-up banner once a course's skills join the CV.
        const courseCounts = ref({})
        const learnSkill = ref(null)
        const planKey = ref(0)
        const banner = ref(null)
        watch(ladder, rows => {
            const ids = rows.filter(r => !r.has && !(r.id in courseCounts.value)).map(r => r.id)
            if (!ids.length) return
            CourseApiService.Counts(ids).then(res => {
                courseCounts.value = { ...courseCounts.value, ...Object.fromEntries(ids.map(id => [id, res.data.counts[id] || 0])) }
            }).catch(() => {})
        })
        function openLearn(row) {
            learnSkill.value = { id: row.id, name: row.name, count: courseCounts.value[row.id] }
        }
        async function onCompleted({ title, added, verified }) {
            const before = target.value ? Math.round(target.value.readiness) : null
            await loadDashboard()
            banner.value = { title, added: added.length, verified, before }
        }

        return {
            router, loading, hasCv, jobs, target, selectedId, selectedRole,
            orderedRoles, visibleRoles, hiddenRoleCount, showAllRoles,
            ladder, ladderFull, ladderVisibleCount,
            addingSkillId, justAddedIds, addMissingSkill,
            pickJobTitle, pickJobResults, pickJobShowDropdown, settingTarget,
            searchJobTitles, onPickJobBlur, setTargetRole,
            RING_C, ringOffset, impPct, readyColor,
            location, includeInterns, courseCounts, learnSkill, planKey, banner, openLearn, onCompleted,
        }
    }
})
</script>

<style scoped>
.tp-dash {
    background: var(--tm-canvas);
    padding: 44px 24px 80px;
    min-height: 70vh;
}

.tp-dash__inner { max-width: 1180px; margin: 0 auto; }

/* Target role names come back from the API in whatever case the taxonomy
   stored them in (lowercase, in practice) — capitalized here the same way
   role/skill names already are everywhere else on this page. */
.tp-dash__role-title { margin: 0; }
.tp-dash__role-title::first-letter { text-transform: uppercase; }

/* ------------------------------------------------------------- states */
.tp-empty { max-width: 560px; padding: 34px; }
.tp-empty__title { font-size: 1.25rem; margin-bottom: 10px; }
.tp-empty p { margin-bottom: 22px; }
.tp-empty__picker { position: relative; max-width: 340px; }

.tp-loading { display: flex; flex-direction: column; gap: 14px; }

.tp-skeleton {
    height: 92px;
    border-radius: var(--tm-radius-md);
    background: linear-gradient(90deg, var(--tm-surface-sunk) 25%, var(--tm-border-soft) 37%, var(--tm-surface-sunk) 63%);
    background-size: 400% 100%;
    animation: tp-shimmer 1.3s ease-in-out infinite;
}

@keyframes tp-shimmer {
    from { background-position: 100% 50%; }
    to { background-position: 0 50%; }
}

@media (prefers-reduced-motion: reduce) {
    .tp-skeleton { animation: none; }
}

/* ------------------------------------------------------------- layout */
.tp-grid {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 28px;
    align-items: start;
}

@media (max-width: 940px) {
    .tp-grid { grid-template-columns: 1fr; }
}

/* ---------------------------------------------------------- readiness */
.tp-readiness { text-align: center; padding: 26px; }

.tp-ring { position: relative; width: 168px; height: 168px; margin: 4px auto 16px; }
.tp-ring svg { transform: rotate(-90deg); }

.tp-ring__arc {
    transition: stroke-dashoffset 1.2s var(--tm-ease);
}

.tp-ring__num {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
}

.tp-ring__pct {
    font-family: var(--tm-font-display);
    font-size: 42px;
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1;
    color: var(--tm-ink);
}

.tp-ring__sign { font-size: 22px; }

.tp-ring__cap {
    font-size: 10.5px;
    color: var(--tm-ink-faint);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-top: 5px;
}


.tp-legend {
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin-top: 20px;
    text-align: left;
}

.tp-legend__row {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 13px;
    color: var(--tm-ink-soft);
}

.tp-legend__key { width: 9px; height: 9px; border-radius: 2.5px; flex: none; }

.tp-legend__n {
    margin-left: auto;
    font-size: 12.5px;
    color: var(--tm-ink);
    font-weight: 600;
}

.tp-readiness__btn { width: 100%; margin-top: 20px; }

/* ------------------------------------------------------------- ladder */
.tp-ladder__head {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.tp-ladder__title { font-size: 21px; font-weight: 700; }

.tp-lrow {
    display: grid;
    grid-template-columns: 24px 1fr 132px 112px;
    gap: 14px;
    align-items: center;
    padding: 13px 12px;
    border-bottom: 1px solid var(--tm-border-soft);
    border-radius: var(--tm-radius-sm);
    transition: background-color 0.16s var(--tm-ease);
}

.tp-lrow:hover { background: var(--tm-surface-sunk); }

@media (max-width: 720px) {
    .tp-lrow { grid-template-columns: 24px 1fr auto auto; }
    .tp-lrow__imp { display: none; }
}

.tp-lrow__rank { font-size: 12px; color: var(--tm-ink-faint); }

.tp-lrow__name {
    font-size: 14.5px;
    font-weight: 500;
    color: var(--tm-ink);
}
.tp-lrow__name::first-letter { text-transform: uppercase; }

.tp-lrow__imp { display: flex; align-items: center; gap: 8px; }
.tp-lrow__imp .tm-meter { flex: 1; }
.tp-lrow__impn { font-size: 11px; color: var(--tm-ink-faint); }

/* -------------------------------------------------------------- roles
   The left column: the desired role's card and every alternative's card
   stack here, in a fixed order that never changes when a different one is
   opened. The ladder on the right is a separate sibling in .tp-grid — it
   never moves, only its content follows whichever card here is open. */
.tp-role-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.tp-role { padding: 20px; }

/* Plain text, not a pill — a colored badge chip here was reading as the
   generic "AI-app" flourish the rest of this design deliberately avoids. */
.tp-target-label {
    display: block;
    font-family: var(--tm-font-mono);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tm-accent);
    margin-bottom: 16px;
}

.tp-target-label--inline { margin: 8px 0 2px; }

.tp-role__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.tp-role__name {
    font-size: 15.5px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--tm-ink);
}
.tp-role__name::first-letter { text-transform: uppercase; }

.tp-role__pct {
    font-family: var(--tm-font-display);
    font-size: 25px;
    font-weight: 800;
    letter-spacing: -0.03em;
}

.tp-role__bar { display: block; margin: 14px 0 11px; }

.tp-role__meta { font-size: 12.5px; }

.tp-rolelist__more {
    align-self: flex-start;
    border: none;
    background: transparent;
    padding: 4px 0;
    font-family: var(--tm-font-sans);
    font-size: 13.5px;
    font-weight: 600;
    color: var(--tm-accent);
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
}

.tp-rolelist__more:hover { color: var(--tm-accent-deep); }

.tp-ladder__more { margin-top: 10px; }
.tp-right { display: flex; flex-direction: column; gap: 18px; min-width: 0; }
.tp-banner {
    display: flex; align-items: center; gap: 14px; margin-bottom: 20px; padding: 14px 18px; border-radius: var(--tm-radius-md);
    background: #E7F6EF; border: 1px solid #A8DEC6; color: var(--tm-ink);
}
.tp-banner__text { flex: 1; display: flex; flex-direction: column; gap: 2px; font-size: 14px; }
.tp-banner__text span { font-size: 13px; color: var(--tm-ink-soft); }
.tp-banner__close { border: 0; background: none; color: var(--tm-ink-soft); cursor: pointer; padding: 6px; }

/* Status column: every chip the same width, flush with the row's right edge. */
.tp-status { justify-self: end; width: 112px; justify-content: center; box-sizing: border-box; }

/* "Missing" at rest; on hover / focus the same pill (same size) splits into
   "+ Add" | "Learn": the label fades out, the halves slide in, a divider grows. */
.tp-split { position: relative; overflow: hidden; padding: 0; }
.tp-split__rest { padding: 3px 0; transition: opacity 0.18s var(--tm-ease), transform 0.18s var(--tm-ease); }
.tp-split__half {
    position: absolute; top: 0; bottom: 0; width: 50%; border: 0; padding: 0; background: none;
    font: inherit; letter-spacing: inherit; text-transform: inherit; color: inherit; cursor: pointer;
    opacity: 0; transition: opacity 0.18s var(--tm-ease), transform 0.22s var(--tm-ease), background-color 0.15s, color 0.15s;
}
.tp-split__half--add { left: 0; transform: translateX(10px); }
.tp-split__half--learn { right: 0; transform: translateX(-10px); }
.tp-split--solo .tp-split__half--add { width: 100%; }
.tp-split::after {
    content: ""; position: absolute; left: 50%; top: 5px; bottom: 5px; width: 1px; background: currentColor;
    opacity: 0.35; transform: scaleY(0); transition: transform 0.22s var(--tm-ease);
}
.tp-split--solo::after { display: none; }
.tp-split:hover .tp-split__rest, .tp-split:focus-within .tp-split__rest { opacity: 0; transform: scale(0.9); }
.tp-split:hover .tp-split__half, .tp-split:focus-within .tp-split__half { opacity: 1; transform: none; }
.tp-split:hover::after, .tp-split:focus-within::after { transform: scaleY(1); }
.tp-split__half:hover, .tp-split__half:focus-visible { background: var(--tm-gap); color: #FFFFFF; outline: none; }
@media (prefers-reduced-motion: reduce) { .tp-split *, .tp-split::after { transition: none; } }

.tp-dash__top { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 28px; }
.tp-dash__filters { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.tp-dash__toggle { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: var(--tm-ink-soft); cursor: pointer; margin: 0; }
.tp-dash__toggle input { accent-color: var(--tm-accent); width: 16px; height: 16px; }
</style>
